<script setup lang="ts">
import {computed, PropType, ref, Ref, watch} from 'vue'
import {DataLoader, InstantDataLoader} from "@/helper/dataLoader.js";
import {
    BookingSuggestion,
    Day,
    DemandJoDaShTiAmIAvailableFragment,
    DemandJoDaShTiFragment, Employee,
    Jobsite,
    ShiftType
} from "@/graphql/generated/graphql";
import Skeleton from "@/components/Skeleton.vue";
import Modal from "@/components/Modal.vue"
import {kapitelDateString} from "@/graphql/kapitelTypes";
import dateHelper from "@/helper/datetime/date";
import {fetchDayForDate} from "@/helper/day";
import datetime from "@/helper/datetime/datetime";
import {
    addAvailabilityCoordinateToDay,
    fetchConsideredFor, fetchWhyDoesntInesConsiderThisDemandJoDaShTi,
    getDemandJoDaShTiForDateRange, getDemandJoDaShTiForDateRangeWithAmIAvailable,
    joDaShTiMatcher,
    removeAvailabilityCoordinateFromDay
} from "@/helper/demandBrowser";
import {claimBookingSuggestion, fetchBookingSuggestion, setBookingSuggestionToSeen} from "@/helper/bookingSuggestion";
import RichResponseListItem from "@/views/Chat/RichResponses/components/RichResponseListItem.vue";
import {clone} from "@/helper/object";
import DayCalendar from "@/views/Chat/RichResponses/DayCalendar.vue";
import JoDaShTiListItem from "@/views/JoDaShTiListItem.vue";
import {useGlobalEmitter} from "@/helper/emitter";
import {IonButton, IonIcon} from "@ionic/vue";
import {KapitelIcon} from "@/helper/icon";
import JobsiteDetails from "@/views/JobsiteDetails.vue";
import ClaimBookingSuggestionConfirmModal from "@/views/components/ClaimBookingSuggestionConfirmModal.vue";

// external model: show
const isOpen = defineModel({type:Boolean})
/* external props:
    date
    jobsite
    shiftTypes - optional, only limiting
 */
const props = defineProps({
    date: {
        type: String as PropType<kapitelDateString>,
        required: true
    },
    jobsite: {
        type: Object as PropType<Jobsite>,
        required: true,
    },
    shiftTypes: {
        type: Array as PropType<ShiftType[]>,
        required: false,
        default: undefined
    },
    dayPrefill: {
        type: Object as PropType<Day>,
        required: false,
    },
    showJobsiteDetailModalLink: {
        type: Boolean,
        required: false,
        default: false
    },
    withdrawalRequestOriginalEmployeeId: {
        type: Number,
        required: false,
    }
})

// preload to save initial request - but clear to allow subsequent updates
const initialDayPrefill : Ref<Day|undefined> = ref()

useGlobalEmitter().on('day:mutated', () => dataLoader.reload());

watch(()=>props.dayPrefill, value => initialDayPrefill.value = value)

const day :Ref<Day|undefined> = ref()
const loadDay = async () => {
    if (initialDayPrefill.value) {
        day.value = initialDayPrefill.value
        initialDayPrefill.value = undefined
    } else {
        day.value = await fetchDayForDate(props.date)
    }
}
const demandJoDaShTis :Ref<(DemandJoDaShTiFragment&DemandJoDaShTiAmIAvailableFragment)[]|undefined> = ref()
const loadAvailabilityCoordinates = async () => demandJoDaShTis.value = await getDemandJoDaShTiForDateRangeWithAmIAvailable(props.jobsite.id, props.date, props.date, props.shiftTypes)

// data loader
const dataLoader = new DataLoader([loadDay, loadAvailabilityCoordinates]);


/**
 * rendersets
 */
const renderSets = computed(() => (demandJoDaShTis.value ?? [])
    .toSorted((c1, c2) => datetime.cmpKapitelDateTime(c1.begin,c2.begin))
    .map((demandJoDaShTi) => {
        const addedAvailability = (day.value?.availability?.additionalAvailabilityCoordinates ?? []).find(a => joDaShTiMatcher(a, demandJoDaShTi))
        const addedWishDemand = (day.value?.wishDemands ?? []).find(w => joDaShTiMatcher(w, demandJoDaShTi))

        const notConsideredLoader = addedAvailability ? InstantDataLoader(async () => fetchWhyDoesntInesConsiderThisDemandJoDaShTi(demandJoDaShTi)) : null
        const awaitNotConsideredResult = (fn:(notConsidered:string|null|undefined)=>any) => computed(() => notConsideredLoader?.pending.value ? undefined : fn(notConsideredLoader?.result.value))

        const suggestionLoader = demandJoDaShTi.instantBookable
            ? InstantDataLoader(async () => {
                const suggestion = await fetchBookingSuggestion(demandJoDaShTi.begin, demandJoDaShTi.until, demandJoDaShTi.jobsite.id, props.withdrawalRequestOriginalEmployeeId)
                if (suggestion && !suggestion.seen && suggestion.request.id) {
                    void setBookingSuggestionToSeen(suggestion.request.id)
                }
                return suggestion
            })
            : undefined

        return {
            demandJoDaShTi,
            notConsideredLoader: notConsideredLoader?.pending,
            notConsideredResult: notConsideredLoader ? awaitNotConsideredResult((msg) => msg) : null,
            amIAvailable: demandJoDaShTi.amIAvailable ?? false,
            toggleable: !demandJoDaShTi.amIAvailable || (!!addedAvailability || !!addedWishDemand),
            toggleState: !!addedAvailability || !!addedWishDemand,
            instantBookable: demandJoDaShTi.instantBookable,
            suggestionLoading: suggestionLoader?.pending ?? computed(() => false),
            suggestion: suggestionLoader
                ? computed(() => suggestionLoader.result.value)
                : computed(() => undefined),
        }
    }))


/**
 * events
 */
const onToggle = async (state: boolean, joDaShTi: DemandJoDaShTiFragment) => {

    if (!day.value) {
        return;
    }

    if (joDaShTi.instantBookable && state) {
        await openClaimSuggestionModal(joDaShTi)
        return
    }

    const newDay = clone(day.value) as Day
    if (state) {
        addAvailabilityCoordinateToDay(joDaShTi, newDay)
    } else {
        removeAvailabilityCoordinateFromDay(joDaShTi, newDay)
    }

    day.value = newDay
}

const showJobsiteDetailsModal = ref(undefined as number|undefined)
const confirmClaimSuggestionModalOpen = ref(false)
const confirmClaimSuggestionSubmitting = ref(false)
const confirmClaimSuggestion = ref<BookingSuggestion | null>(null)
const confirmClaimSuggestionJoDaShTi = ref<DemandJoDaShTiFragment | null>(null)

const openClaimSuggestionModal = async (joDaShTi: DemandJoDaShTiFragment) => {
    confirmClaimSuggestionJoDaShTi.value = joDaShTi
    confirmClaimSuggestion.value = await fetchBookingSuggestion(
        joDaShTi.begin,
        joDaShTi.until,
        joDaShTi.jobsite.id,
        props.withdrawalRequestOriginalEmployeeId
    )
    confirmClaimSuggestionModalOpen.value = true
}

const onConfirmClaimSuggestion = async () => {
    const joDaShTi = confirmClaimSuggestionJoDaShTi.value
    if (!joDaShTi || !day.value) {
        return
    }

    confirmClaimSuggestionSubmitting.value = true
    try {
        await claimBookingSuggestion(
            joDaShTi.begin,
            joDaShTi.until,
            joDaShTi.jobsite.id,
            props.withdrawalRequestOriginalEmployeeId
        )
        confirmClaimSuggestionModalOpen.value = false
        useGlobalEmitter().emit('day:mutated', day.value.date)
    } finally {
        confirmClaimSuggestionSubmitting.value = false
    }
}
</script>

<template>
    <Modal
        v-model="isOpen"
        @view:willPresent="dataLoader.reload"
        :transparentToolbar="true"
    >

        <h2 class="globalHeaderWithSubHeader">{{dateHelper.formatKapitelDate(props.date)}}</h2>
        <h4 class="globalHeaderWithSubHeader">
            auf {{jobsite?.abbreviation}}
            <a v-if="showJobsiteDetailModalLink" @click="showJobsiteDetailsModal = jobsite.id">
                <IonIcon :icon="KapitelIcon.infoFill"  class="globalTextAlignedIcon"/>
            </a>
        </h4>

        <div>
            <h3>Mein Kalender</h3>
            <DayCalendar :show-icon="true" :init-data="props.date"/><br>

            <h3>Offene Dienste</h3>
            <Skeleton v-if="dataLoader.pending.value" :items="['block--height-5em']"></Skeleton>
            <div v-if="!dataLoader.pending.value && renderSets.length > 0">

                <div  v-for="rs in renderSets">



                    <JoDaShTiListItem
                        :jo-da-sh-ti ="rs.demandJoDaShTi"
                        :amIAvailable="rs.amIAvailable"
                        :toggleable="rs.toggleable"
                        :toggle-state="rs.toggleState"
                        :instant-bookable="rs.instantBookable"
                        :suggestion-loading="rs.suggestionLoading.value"
                        :suggestion="rs.suggestion.value"
                        v-on:toggle="onToggle"
                    />


                    <p v-if="!rs.notConsideredLoader?.value && rs.notConsideredResult?.value">Achtung: {{rs.notConsideredResult?.value}}</p>

    <!--                :jobsite-label="rs.jobsiteLabel"-->


    <!--                <h1>{{rs.demandJoDaShTi.shiftType}} {{rs.demandJoDaShTi.begin}}</h1>-->

    <!--                <IonButton v-if="isSet(rs.addedAvailability)" @click="() => isSet(rs.addedAvailability) ? removeCoordinateFromAvailability(rs.addedAvailability) : null">Wird berücksichtigt - jetzt entfernen<IonIcon :icon="removeCircle"></IonIcon></IonButton>-->
    <!--                <div v-else>-->
    <!--                    <Skeleton v-if="rs.considered.pending.value" :items="['block']"/>-->
    <!--                    <IonButton v-else-if="!rs.considered.result" @click="() => addCoordinateToAvailability(rs.demandJoDaShTi)">Nicht berücksichtigt - jetzt hinzufügen</IonButton>-->
    <!--                    <h1 v-else>Bereits durch Monatsplanung berücksichtigt</h1>-->
    <!--                </div>-->
                </div>
            </div>
            <div v-if="!dataLoader.pending.value && renderSets.length == 0">
                <RichResponseListItem
                    style="height: 5em"
                    :text="`Keine offenen Dienste` /* am ${dateHelper.formatKapitelDate(props.date)} auf ${jobsite?.abbreviation}*/"
                />
            </div>
        </div>

        <JobsiteDetails v-model="showJobsiteDetailsModal" :showJobsiteDemandModalLink="false" />
        <ClaimBookingSuggestionConfirmModal
            v-model="confirmClaimSuggestionModalOpen"
            :messages="confirmClaimSuggestion?.gapToInstantBookableCheckMessages ?? []"
            :submitting="confirmClaimSuggestionSubmitting"
            @confirm="onConfirmClaimSuggestion"
        />

        <template #actions="{dismiss}">
            <IonButton color="primary" @click="dismiss">Zurück</IonButton>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.calendar-date-content {
    $SIZE: 0.5em;
    width: $SIZE;
    height: $SIZE;
    border-radius: 50%;
    transform: translateY(1.1em);

    &.demand {
        background-color: var(--ion-color-red);
    }
}
</style>
